import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, ValidationErrors, AbstractControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ToastContainerDirective } from 'ngx-toastr';
import { extendDeep, flattenObject } from '@utils/utils';
import { User } from '@models/user';
import { AtlasToastService } from '@services/atlas-toast.service';
import { UserService } from '@services/user.service';
import { Location } from '@angular/common';

function customExactLength(): (AbstractControl) => ValidationErrors | null {
  return (control: AbstractControl): ValidationErrors | null => {
    return control.value.length === 11 ? null : { billingVatCodeLength: true };
  };
}

// custom validator maxlength must not superior to 2
function customMaxLength(): (AbstractControl) => ValidationErrors | null {
  return (control: AbstractControl): ValidationErrors | null => {
    return control.value.length <= 2 ? null : { maxLengthProvince: true };
  };
}

@Component({
  selector: 'wsb-cart-billing',
  templateUrl: './cart-billing.component.html',
  styleUrls: ['./cart-billing.component.scss'],
  standalone: false
})
export class CartBillingComponent implements OnInit, OnDestroy {
  @ViewChild(ToastContainerDirective) toastContainer: ToastContainerDirective;
  @ViewChild('actionsBar', { static: true }) $actionsBar;

  billingForm: UntypedFormGroup = null;
  user: User | null;
  isPrivateUser = false;
  routeStep = { 1: '/shop/cart', 2: '/shop/cart-summary' };
  currentStep = 10; //fake step

  private userSubscription: Subscription;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private userService: UserService,
    private atlasToastService: AtlasToastService,
    private location: Location
  ) {}

  ngOnInit(): void {
    this.billingForm = this.formBuilder.group({
      billing: this.formBuilder.group({
        address: ['', [Validators.required]],
        city: ['', [Validators.required]],
        companyName: ['', Validators.maxLength(40)],
        province: [''],
        taxCode: [''],
        vatCode: [''],
        paCode: [''],
        zipCode: ['', [Validators.required]],
        invoicingEmail: ['', [Validators.email]]
      })
    });

    this.userSubscription = this.userService.getAuthenticationState().subscribe(loggedUser => {
      this.user = loggedUser;
      if (this.user.account.type === 'Private') {
        this.isPrivateUser = true;
      }
      this.billingForm.patchValue(loggedUser);

      if (!this.isPrivateUser) {
        this.billingForm
          .get('billing')
          .get('vatCode')
          .setValidators(this.user.nation === 'ita' ? [Validators.required, customExactLength()] : []);

        this.billingForm
          .get('billing')
          .get('taxCode')
          .setValidators(this.user.nation === 'ita' ? [Validators.required] : []);

        this.billingForm
          .get('billing')
          .get('companyName')
          .setValidators(Validators.required);

        this.billingForm.get('billing.taxCode').valueChanges.subscribe(() => {
          const taxCodeFirstChar = this.billingForm
            .get('billing.taxCode')
            .value?.toString()
            .charAt(0);
          if (['8', '9'].includes(taxCodeFirstChar)) {
            this.billingForm.get('billing.vatCode').clearValidators();
            this.billingForm.get('billing.vatCode').updateValueAndValidity();
          } else {
            this.billingForm.get('billing.vatCode').setValidators([Validators.required, customExactLength()]);
            this.billingForm.get('billing.vatCode').updateValueAndValidity();
          }
        });
      }

      this.billingForm
        .get('billing')
        .get('province')
        .setValidators(this.user.nation === 'ita' ? [Validators.pattern('[a-zA-Z]*'), Validators.minLength(2), customMaxLength()] : []);

      this.billingForm.get('billing.province').valueChanges.subscribe(() => {
        this.billingForm
          .get('billing.province')
          .patchValue(this.billingForm.get('billing.province').value.toUpperCase(), { emitEvent: false });
      });
    });
  }

  submitBillingForm() {
    if (this.billingForm.invalid) {
      this.billingForm.enable();
      return;
    }

    const formObject = flattenObject(this.user) as any;
    extendDeep(formObject, this.billingForm.value); // User object from api may change, and we never delete any property
    delete formObject.password; // not really used
    // new
    this.userService.updateUserBilling(this.user, formObject.billing).subscribe(
      savedBilling => {
        this.user.billing = savedBilling;
        this.billingForm.reset(this.user);
        this.atlasToastService.success('common_success', null, 6000, this.toastContainer);
        this.billingForm.enable();
        this.userService.updateBillingDetails.emit(true);

        // this.disablePasswordField();
      },
      error => {
        this.atlasToastService.error('common_error', null, 6000, this.toastContainer);
        this.billingForm.enable();
        // this.disablePasswordField();
      }
    );
    // old
    // this.userService.updateUserProfile(this.user, formObject).subscribe(
    //   savedUser => {
    //     this.user = savedUser;
    //     this.billingForm.reset(this.user);
    //     this.atlasToastService.success('common_success', null, 6000, this.toastContainer);
    //     this.billingForm.enable();
    //     this.userService.updateBillingDetails.emit(true);

    //     // this.disablePasswordField();
    //   },
    //   error => {
    //     this.atlasToastService.error('common_error', null, 6000, this.toastContainer);
    //     this.billingForm.enable();
    //     // this.disablePasswordField();
    //   }
    // );
  }

  resetForm() {
    this.billingForm.reset(this.user);
  }

  goBack() {
    this.location.back();
  }

  ngOnDestroy(): void {
    this.userSubscription.unsubscribe();
  }
}
